import { Box, Link, Paper, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useIsMobile, useL10n } from '@uplift/hooks';
import { EditableSectionRow } from '../../../../EditableSection';
import { PhoneForm } from './PhoneForm.component';
import { LinkButton } from '../../../../Button';
import { Alert } from '../../../../Alert';
import { VerificationTag } from '../SettingsComponents';
import { ContactName } from '@uplift/types';


export const usePhoneSettingsRowStyles = makeStyles()((theme) => ({
  fontWeight500: {
    fontWeight: 500
  },
  phoneTable: {
    display: 'flex',
    flexDirection: 'column',
    '& > *:not(:last-child)': {
      paddingBottom: theme.spacing(2)
    },
  },
  mobile: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gridGap: theme.spacing(2)
  },
  textOptInStatus: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.secondary.background,
    borderColor: theme.palette.secondary.border,
  },
  textOptInStatusTitle: {
    fontWeight: 600,
  }
}));

export type PhoneSettingsRowProps = {
  rowKey: string;
  showWorkContact?: boolean;
  contacts: {
    [ContactName.mobile]?: string;
    [ContactName.work]?: string;
    [ContactName.home]?: string;
  };
  mobileVerified?: boolean;
  showVerifyAlert?: boolean;
  onVerifyClick?: () => void;
};


export const PhoneSettingsRow: React.VFC<PhoneSettingsRowProps> = ({ contacts, showWorkContact, ...props }) => {
  const l10n = useL10n();
  const { classes } = usePhoneSettingsRowStyles();
  const isMobile = useIsMobile('sm');

  const alert = props.showVerifyAlert && (props.mobileVerified === false);
  const verifyMobileClick = isMobile && alert ? props.onVerifyClick : undefined;
  const phones = [
    {
      label: l10n.providerSettings.mobile,
      value: contacts[ContactName.mobile],
    },
    {
      label: l10n.providerSettings.work,
      value: contacts[ContactName.work]
    },
    {
      label: l10n.providerSettings.home,
      value: contacts[ContactName.home]
    },
  ].filter(phone => !!phone.value);

  const content = (
    <Box onClick={verifyMobileClick} className={classes.phoneTable}>
      {phones.map(({ label, value }) => (
        <Box key={label} display="flex" flexDirection="column" gap="1px">
          <Typography variant="body1" color="textSecondary">{label}</Typography>
          <Box className={classes.mobile}>
            <Typography className={classes.fontWeight500}>
              {value}
            </Typography>
            {value === contacts[ContactName.mobile] && <VerificationTag verified={props.mobileVerified ?? false} />}
          </Box>
          {value === contacts[ContactName.mobile] && alert && (
            <Box mt={1} mb={1} maxWidth="486px">
              <Alert severity="warning">
                {l10n.providerSettings.unverified} <LinkButton variant="body2" color="textPrimary" onClick={props.onVerifyClick}>
                  {l10n.providerSettings.verify}
                </LinkButton>
              </Alert>
            </Box>
          )}
          {value === contacts[ContactName.mobile] && props.mobileVerified && (
            <Box mt={1} mb={1} maxWidth="486px">
              <Paper variant="outlined" className={classes.textOptInStatus}>
                <Typography variant="body2" color="textPrimary" className={classes.textOptInStatusTitle} gutterBottom>
                  {l10n.providerSettings.textOptInStatus.title}
                </Typography>
                <Typography variant="body2" color="textPrimary">
                  {l10n.providerSettings.textOptInStatus.body} {l10n.providerSettings.textOptInStatus.seeOur}{' '}
                  <Link href={l10n.general.privacyUrl} color="inherit" underline="always" target="_blank" rel="noreferrer">
                    {l10n.general.privacy}
                  </Link> and <Link href={l10n.general.termsUrl} color="inherit" underline="always" target="_blank" rel="noreferrer">
                    {l10n.general.terms}
                  </Link>.
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );

  if (isMobile && alert) {
    return (
      <EditableSectionRow
        label={l10n.providerSettings.phone}
        kind="custom"
        {...props}
      >
        {content}
      </EditableSectionRow>
    );
  }

  return (
    <EditableSectionRow
      label={l10n.providerSettings.phone}
      form={<PhoneForm showWorkContact={showWorkContact} />}
      kind="editable-row"
      {...props}
    >
      {content}
    </EditableSectionRow>
  );
};
